<template>
  <div class="apt-sidebar">
    <!-- Header Section -->
    <div class="sidebar-header">
      <div class="header-content">
        <div class="patient-avatar">
          <i class="fa fa-user"></i>
        </div>
        <div class="patient-info">
          <h2 class="patient-name">{{ apt.Patient }}</h2>
          <div class="appointment-meta">
            <span class="meta-item">
              <i class="fa fa-calendar"></i>
              {{ apt.AptDateTime | dateformatwithtz(allSettings.GENERAL_TIMEZONE.PropertyValue, dateFormatStr) }}
            </span>
            <span class="meta-item">
              <i class="fa fa-clock-o"></i>
              {{ apt.AptDateTime | timeformatwithtz(allSettings.GENERAL_TIMEZONE.PropertyValue) }}
            </span>
          </div>
        </div>
      </div>
      <button class="close-btn" @click="closeSlideOutPanel" title="Close">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <PopupErrorDisplay/>

    <!-- Main Content -->
    <div class="sidebar-content">
      <!-- Appointment Details Card -->
      <div class="detail-card">
        <div class="card-header">
          <i class="fa fa-info-circle"></i>
          <h3>Appointment Details</h3>
        </div>
        <div class="card-body">
          <div class="detail-row">
            <div class="detail-label">
              <i class="fa fa-medkit"></i>
              <span>Operatory</span>
            </div>
            <div class="detail-value">{{ apt.OpName || 'N/A' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-label">
              <i class="fa fa-stethoscope"></i>
              <span>Procedures</span>
            </div>
            <div class="detail-value">{{ apt.Procedures || 'None specified' }}</div>
          </div>
          <div class="detail-row" v-if="apt.Note">
            <div class="detail-label">
              <i class="fa fa-sticky-note"></i>
              <span>Note</span>
            </div>
            <div class="detail-value note-text">{{ apt.Note }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-label">
              <i class="fa fa-check-circle"></i>
              <span>Status</span>
            </div>
            <div class="detail-value">
              <span class="status-badge" :class="getStatusClass(apt.OpenDentalAptConfirmStatus)">
                {{ apt.OpenDentalAptConfirmStatus || 'Unknown' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Activity Timeline Card -->
      <div class="detail-card">
        <div class="card-header">
          <i class="fa fa-history"></i>
          <h3>Reminder Activity</h3>
          <span class="activity-count" v-if="aptDetailData && aptDetailData.length">
            {{ aptDetailData.length }} {{ aptDetailData.length === 1 ? 'activity' : 'activities' }}
          </span>
        </div>
        <div class="card-body">
          <!-- Loading State -->
          <div class="loading-state" v-if="loadingStatus">
            <div class="spinner"></div>
            <p>Loading activity...</p>
          </div>

          <!-- Empty State -->
          <div class="empty-state" v-else-if="!aptDetailData || aptDetailData.length === 0">
            <div class="empty-icon">
              <i class="fa fa-inbox"></i>
            </div>
            <p>No reminder activity found</p>
          </div>

          <!-- Activity Timeline -->
          <div class="activity-timeline" v-else>
            <div
              class="timeline-item"
              v-for="(row, key) in aptDetailData"
              :key="key"
              :class="getActivityClass(row)"
            >
              <div class="timeline-icon" :class="getIconClass(row.ReminderType)">
                <i :class="getActivityIcon(row.ReminderType)"></i>
              </div>
              <div class="timeline-content">
                <div class="timeline-header">
                  <span class="activity-type">{{ getActivityLabel(row.ReminderType) }}</span>
                  <span class="activity-status" :class="getStatusBadgeClass(row.Status)">
                    {{ row.Status }}
                  </span>
                </div>
                <div class="timeline-details">
                  <!--
                    A REFUSED reminder (opted out, number can't receive texts, no email on file…)
                    carries a SentOn stamp too — that stamp is what stops the exe re-sending it every
                    5 minutes — so the date alone would read as "Sent". Status says what happened.
                  -->
                  <div class="detail-item refused" v-if="isRefusedRow(row)">
                    <i class="fa fa-ban"></i>
                    <span>Not sent: {{ getSentDate(row) }}</span>
                  </div>
                  <div class="detail-item" v-else>
                    <i class="fa fa-paper-plane"></i>
                    <span>Sent: {{ getSentDate(row) }}</span>
                  </div>
                  <!--
                    A decline sets ConfirmedOn too (appt-reminder-service stamps it for YES and NO
                    alike), so keying purely off ConfirmedOn used to show a green "Confirmed" check
                    on a patient who had just asked to cancel. Check the response first.
                  -->
                  <div class="detail-item declined" v-if="isDeclineRow(row)">
                    <i class="fa fa-times-circle"></i>
                    <span>Cancellation requested: {{ row.ConfirmedOn | dateformatwithtz(allSettings.GENERAL_TIMEZONE.PropertyValue, dateFormatStr) }}</span>
                  </div>
                  <div class="detail-item" v-else-if="row.ConfirmedOn">
                    <i class="fa fa-check"></i>
                    <span>Confirmed: {{ row.ConfirmedOn | dateformatwithtz(allSettings.GENERAL_TIMEZONE.PropertyValue, dateFormatStr) }}</span>
                  </div>
                  <div class="detail-item" v-if="row.ConfirmResponse">
                    <i class="fa fa-comment"></i>
                    <span>Response: "{{ row.ConfirmResponse }}"</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */

import Widget from '@/components/Widget/Widget';
import mixin from '../../../mixin.js';
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import { mapActions } from 'vuex';

export default {
    name: 'AptReminderSidebar',
    components: {Widget, PopupErrorDisplay},
    props: ['apt'],
    mixins: [mixin],

    async created() {
        await this.init();
    },

    data(){
        return {
            loadingStatus: true,
            aptDetailData: []
        }
    },

    methods: {

        ...mapActions('aptreminder', ['getAptReminderDetailData']),

        async init() {
            this.resetError();
            this.loadingStatus = true;
            let payload = {
                siteID: this.siteID,
                aptNum: this.apt.AptNum,
                patNum: this.apt.PatNum,
            };
            console.dir(payload);
            this.aptDetailData = await this.getAptReminderDetailData(payload);
            console.dir(this.aptDetailData);
            this.loadingStatus = false;
        },

        async closeSlideOutPanel() {
          this.$emit('closePanel',this.goals);
        },

        async save() {
            this.$emit('closePanel',this.goals);
        },

        getActivityIcon(reminderType) {
            switch(reminderType) {
                case 'TEXT_SAME_DAY_REMINDER':
                case 'TEXT_REMINDERS':
                case 'TEXT_CONFIRMATIONS':
                case 'PREMED_REMINDERS':
                    return 'fa fa-mobile';
                case 'EMAIL_REMINDERS':
                case 'EMAIL_CONFIRMATIONS':
                    return 'fa fa-envelope';
                default:
                    return 'fa fa-bell';
            }
        },

        getActivityLabel(reminderType) {
            switch(reminderType) {
                case 'TEXT_SAME_DAY_REMINDER': return 'Same Day Text Reminder';
                case 'PREMED_REMINDERS': return 'Premed Reminder';
                case 'EMAIL_REMINDERS': return 'Email Reminder';
                case 'TEXT_REMINDERS': return 'Text Reminder';
                case 'EMAIL_CONFIRMATIONS': return 'Email Confirmation';
                case 'TEXT_CONFIRMATIONS': return 'Text Confirmation';
                default: return 'Reminder';
            }
        },

        getIconClass(reminderType) {
            if (reminderType && reminderType.includes('EMAIL')) {
                return 'email-icon';
            } else if (reminderType && reminderType.includes('TEXT')) {
                return 'text-icon';
            }
            return 'default-icon';
        },

        /*
         * Did this patient click "I need to Cancel" in the reminder email?
         *
         * Exact match on 'NO' — the literal the email confirm link encodes. Deliberately NOT
         * "anything that isn't a confirm keyword": the same ConfirmResponse column also stores
         * free-text SMS replies ('thanks!', whole sentences), and those are not cancellations.
         * Mirrors isDeclineResponse() in appt-reminder-service/lib/decline.js — keep in sync.
         */
        isDeclineRow(row) {
            if (!row || row.ConfirmResponse === null || row.ConfirmResponse === undefined) return false;
            return String(row.ConfirmResponse).trim().toUpperCase() === 'NO';
        },

        /**
         * A refused send. teamio-utility writes these Status values (planNonDeliveryOutcome for
         * texts: 'Opted out' / 'Not sent (…)'; describeEmailTargetProblem for email: 'Email not
         * sent (…)' / legacy 'Could not send Email …'). Keep the prefixes in sync with utility.
         */
        isRefusedStatus(status) {
            return !!status && /^(Opted out|Not sent|Email not sent|Could not send)/i.test(String(status));
        },

        isRefusedRow(row) {
            return !!row && this.isRefusedStatus(row.Status);
        },

        getActivityClass(row) {
            if (this.isDeclineRow(row)) {
                return 'error';
            }
            if (this.isRefusedRow(row)) {
                return 'error';
            }
            if (row.Status === 'DELIVERED' || row.Status === 'CONFIRMED') {
                return 'success';
            } else if (row.Status === 'FAILED' || row.Status === 'ERROR') {
                return 'error';
            } else if (row.Status === 'PENDING') {
                return 'pending';
            }
            return '';
        },

        getStatusBadgeClass(status) {
            if (this.isRefusedStatus(status)) {
                return 'error';
            }
            if (status === 'DELIVERED' || status === 'CONFIRMED' || status === 'SENT') {
                return 'success';
            } else if (status === 'FAILED' || status === 'ERROR') {
                return 'error';
            } else if (status === 'PENDING') {
                return 'pending';
            }
            return 'default';
        },

        getStatusClass(status) {
            if (!status) return 'default';
            const lower = status.toLowerCase();
            if (lower.includes('confirm')) return 'confirmed';
            if (lower.includes('unconfirm') || lower.includes('not')) return 'unconfirmed';
            return 'default';
        },

        getSentDate(row) {
            let sentOn;
            switch(row.ReminderType) {
                case 'TEXT_SAME_DAY_REMINDER':
                case 'PREMED_REMINDERS':
                case 'TEXT_REMINDERS':
                case 'TEXT_CONFIRMATIONS':
                    sentOn = row.SmsSentOn;
                    break;
                case 'EMAIL_REMINDERS':
                case 'EMAIL_CONFIRMATIONS':
                    sentOn = row.EmailSentOn;
                    break;
                default:
                    sentOn = row.SmsSentOn || row.EmailSentOn;
            }
            return this.formatDateTimeWitTz(sentOn, this.allSettings.GENERAL_TIMEZONE.PropertyValue);
        },

        getTextToPrint(row) {
            let textString, sentOn;
            switch(row.ReminderType) {
                case 'TEXT_SAME_DAY_REMINDER': textString = 'Same day text reminder'; sentOn = row.SmsSentOn; break;
                case 'PREMED_REMINDERS': textString = 'Premed reminder text'; sentOn = row.SmsSentOn; break;
                case 'EMAIL_REMINDERS': textString = 'Email reminder'; sentOn = row.EmailSentOn; break;
                case 'TEXT_REMINDERS': textString = 'Text reminder'; sentOn = row.SmsSentOn; break;
                case 'EMAIL_CONFIRMATIONS': textString = 'Email confirmation'; sentOn = row.EmailSentOn; break;
                case 'TEXT_CONFIRMATIONS': textString = 'Confirmation text'; sentOn = row.SmsSentOn; break;
                default: break;
            }
            const when = this.formatDateTimeWitTz(sentOn, this.allSettings.GENERAL_TIMEZONE.PropertyValue);
            return this.isRefusedRow(row)
                ? `${textString} not sent — ${row.Status} (${when})`
                : `${textString} sent on ${when}`;
        }
    },

    computed: {
    }
}

</script>

<style lang="scss" scoped>
// Appointment Reminder Sidebar - Modern Design

.apt-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, sans-serif;
}

// Header Section
.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: relative;

  .header-content {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
  }

  .patient-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .fa {
      font-size: 28px;
      color: white;
    }
  }

  .patient-info {
    flex: 1;
    min-width: 0;

    .patient-name {
      font-size: 22px;
      font-weight: 700;
      margin: 0 0 8px 0;
      color: white;
      line-height: 1.2;
    }

    .appointment-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        opacity: 0.9;

        .fa {
          font-size: 13px;
        }
      }
    }
  }

  .close-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: scale(1.1);
    }

    .fa {
      font-size: 16px;
    }
  }
}

// Main Content
.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
}

// Detail Card
.detail-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  overflow: hidden;

  .card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;

    .fa {
      color: #3abf94;
      font-size: 18px;
    }

    h3 {
      flex: 1;
      font-size: 16px;
      font-weight: 600;
      color: #2d3748;
      margin: 0;
    }

    .activity-count {
      font-size: 12px;
      font-weight: 500;
      color: #718096;
      background: #edf2f7;
      padding: 4px 10px;
      border-radius: 12px;
    }
  }

  .card-body {
    padding: 20px;
  }
}

// Detail Rows
.detail-row {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }

  .detail-label {
    width: 130px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #718096;
    flex-shrink: 0;

    .fa {
      width: 16px;
      text-align: center;
      color: #3abf94;
      font-size: 14px;
    }
  }

  .detail-value {
    flex: 1;
    font-size: 14px;
    color: #2d3748;
    line-height: 1.5;

    &.note-text {
      font-style: italic;
      color: #4a5568;
    }
  }
}

// Status Badge
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;

  &.confirmed {
    background: #d1fae5;
    color: #065f46;
  }

  &.unconfirmed {
    background: #fef3c7;
    color: #92400e;
  }

  &.default {
    background: #edf2f7;
    color: #4a5568;
  }
}

// Loading State
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #3abf94;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  p {
    font-size: 14px;
    color: #718096;
    margin: 0;
  }
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

// Empty State
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;

  .empty-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;

    .fa {
      font-size: 24px;
      color: #a0aec0;
    }
  }

  p {
    font-size: 14px;
    color: #718096;
    margin: 0;
  }
}

// Activity Timeline
.activity-timeline {
  .timeline-item {
    display: flex;
    gap: 16px;
    padding: 16px 0;
    border-bottom: 1px solid #f1f5f9;
    position: relative;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    &:first-child {
      padding-top: 0;
    }

    // Vertical line connector
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      left: 19px;
      top: 52px;
      bottom: -16px;
      width: 2px;
      background: #e2e8f0;
    }

    .timeline-icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      position: relative;
      z-index: 1;

      .fa {
        font-size: 16px;
        color: white;
      }

      &.text-icon {
        background: linear-gradient(135deg, #3abf94, #2d9474);
      }

      &.email-icon {
        background: linear-gradient(135deg, #667eea, #764ba2);
      }

      &.default-icon {
        background: linear-gradient(135deg, #718096, #4a5568);
      }
    }

    .timeline-content {
      flex: 1;
      min-width: 0;

      .timeline-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 10px;
        flex-wrap: wrap;

        .activity-type {
          font-size: 14px;
          font-weight: 600;
          color: #2d3748;
        }

        .activity-status {
          display: inline-flex;
          align-items: center;
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;

          &.success {
            background: #d1fae5;
            color: #065f46;
          }

          &.error {
            background: #fee2e2;
            color: #991b1b;
          }

          &.pending {
            background: #fef3c7;
            color: #92400e;
          }

          &.default {
            background: #edf2f7;
            color: #4a5568;
          }
        }
      }

      .timeline-details {
        .detail-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #4a5568;
          margin-bottom: 6px;
          line-height: 1.4;

          &:last-child {
            margin-bottom: 0;
          }

          .fa {
            width: 14px;
            text-align: center;
            color: #718096;
            font-size: 12px;
            flex-shrink: 0;
          }

          // "Cancellation requested" — must read as the opposite of the green confirm check,
          // because a decline shares the same ConfirmedOn timestamp a confirmation has.
          &.declined {
            color: #9b1c1c;
            font-weight: 600;

            .fa {
              color: #dc2626;
            }
          }

          &.refused {
            color: #9b1c1c;
            font-weight: 600;

            .fa {
              color: #dc2626;
            }
          }
        }
      }
    }

    // Success state styling
    &.success {
      .timeline-content {
        .activity-type {
          color: #065f46;
        }
      }
    }

    // Error state styling
    &.error {
      .timeline-icon {
        background: linear-gradient(135deg, #ef4444, #dc2626) !important;
      }
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .sidebar-header {
    padding: 20px;

    .header-content {
      gap: 12px;
    }

    .patient-avatar {
      width: 50px;
      height: 50px;

      .fa {
        font-size: 22px;
      }
    }

    .patient-info {
      .patient-name {
        font-size: 18px;
      }

      .appointment-meta {
        flex-direction: column;
        gap: 6px;

        .meta-item {
          font-size: 13px;
        }
      }
    }
  }

  .sidebar-content {
    padding: 16px;
  }

  .detail-card {
    .card-header {
      padding: 14px 16px;
    }

    .card-body {
      padding: 16px;
    }
  }

  .detail-row {
    flex-direction: column;
    gap: 4px;

    .detail-label {
      width: 100%;
    }

    .detail-value {
      padding-left: 24px;
    }
  }

  .activity-timeline {
    .timeline-item {
      gap: 12px;

      .timeline-icon {
        width: 36px;
        height: 36px;

        .fa {
          font-size: 14px;
        }
      }

      &:not(:last-child)::after {
        left: 17px;
        top: 48px;
      }

      .timeline-content {
        .timeline-header {
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .sidebar-header {
    padding: 16px;

    .patient-avatar {
      width: 44px;
      height: 44px;

      .fa {
        font-size: 20px;
      }
    }

    .patient-info {
      .patient-name {
        font-size: 16px;
      }
    }

    .close-btn {
      width: 32px;
      height: 32px;
    }
  }
}
</style>
